// Libs
import React, {useEffect, useMemo, useState} from 'react';
import formatDate from "../../Utils/Date/formatDate";
import {useTranslation} from "react-i18next";

// utils
import axios from "../../../axios-defaults";
import {DEFAULT_DATE, TODAY, TODAY_MINUS_14, TODAY_MINUS_30} from "../../Accounts/Account/Tables/DateFilters";

// Components
import Tabs from "../../UI/Tabs/Tabs";
import ConsumptionTab from "./Consumption";
import SignalTab from "./Signal";
import LevelTab from "./Level";
import StatsTab from "./Statistics";
import FillingsTab from "./Fillings";


const TankTabs = (props) => {
	const { t } = useTranslation();
	const { tank, readingDevice } = props;

	//////////////////////////////////////////////////////////////////////////////////////////
	// State management //////////////////////////////////////////////////////////////////////
	// Loading and errors
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [unparsedData, setUnparsedData] = useState([]);
	const [totalPages, setTotalPages] = useState(0);

	// Tabs settings
	const today = new Date();
	const [currentTab,setCurrentTab] = useState(null);
	const [dateConfigs,setDateConfigs] = useState({
		isSpecific : false,
		startDate : null,
		endDate : today.toISOString()[0]+"T23:59:59.999Z",
		dateFilterType : null,
	})
	//////////////////////////////////////////////////////////////////////////////////////////
	//////////////////////////////////////////////////////////////////////////////////////////

	const getReadingData = async () => {
		if (dateConfigs.startDate === null){
			return;
		}

		return await axios.get("/tanks/getDipReadings", {
			params: {
				dipReadingDeviceNumber: readingDevice.dipReadingDeviceNumber,
				startDate: (dateConfigs.startDate) ? dateConfigs.startDate : DEFAULT_DATE,
				endDate: dateConfigs.endDate
			}
		}).then(response => {
			if (response.data.succeeded){


				const parsedData = uniqueDayOccurence(response.data.data);

				setTotalPages(response.data.totalPages);
				setUnparsedData(response.data.data.map( (item,index) => {
					return {
						...item,
						'inventory': tank.capacity
					}
				}));

				return parsedData;
			}
			return [];
		}).catch(error => {
			console.log('[TankTabs.jsx] Ajax error in getReadingData() ', error);
			return [];
		});
	}

	const uniqueDayOccurence = (dataToParse) => {
		// Might look a bit complicated but this method do the following:
		// Keeping only the non-duplicate entries and the last occurrence of each unique `lastRead` property date.
		// While also calculating the average temperature for each unique lastRead date
		// And finally adding a new property `inventory` which is a value forwarded by the current `tank` data to be used in the statistics.jsx tab.

		let parsedData = dataToParse.reduce((accumulator, obj) => {
			const existingObjIndex = accumulator.findIndex((item) => {
				const itemDate = new Date(item.lastRead).toISOString().split("T")[0];
				const objDate = new Date(obj.lastRead).toISOString().split("T")[0];

				return itemDate === objDate;
			});

			if (existingObjIndex !== -1) {
				accumulator[existingObjIndex].temperatures.push(obj.temperature);
			} else {
				accumulator.push({
					...obj,
					temperatures: [obj.temperature],
					inventory: tank.capacity
				});
			}

			return accumulator;
		}, []);

		parsedData = parsedData.map((obj) => {
			const temperatureSum = obj.temperatures.reduce((sum, temperature) => sum + temperature, 0);
			const temperatureAverage = temperatureSum / obj.temperatures.length;
			delete obj.temperatures;
			obj.temperature = temperatureAverage;
			return obj;
		});

		return parsedData;
	}

	const setStartDate = (date) => {
		if (date != null) {
			const dateFormatted = `${date.toISOString().split("T")[0]}T00:00:00.000Z`;
			setDateConfigs((prevState) => ({
				...prevState,
				startDate: dateFormatted,
			}));
		}
	}

	const setEndDate = (date) => {
		if(date != null){
			const dateFormatted = `${date.toISOString().split("T")[0]}T23:59:59.999Z`;
			setDateConfigs((prevState) => ({
				...prevState,
				endDate: dateFormatted,
			}));
		}
	}

	const setLast14Days = () => {
		setDateConfigs((prevState) => ({
			...prevState,
			startDate: formatDate(new Date(TODAY_MINUS_14)),
			endDate: formatDate(TODAY,'23:59:59.999Z'),
			isSpecific: false
		}));
	}

	const setLast30Days = () => {
		setDateConfigs((prevState) => ({
			...prevState,
			startDate: formatDate(new Date(TODAY_MINUS_30)),
			endDate: formatDate(TODAY,'23:59:59.999Z'),
			isSpecific: false
		}));
	}

	const setSpecificDate = () => {
		setDateConfigs((prevState) => ({
			...prevState,
			startDate: null,
			endDate: null,
			isSpecific: true
		}));
	}

	const setDateFilterType = (type) => {
		setDateConfigs((prevState) => ({
			...prevState,
			dateFilterType: type,
		}));

		switch (type) {
			case 'last_14': setLast14Days(); break;
			case 'last_30': setLast30Days(); break;
			case 'specific_date': setSpecificDate(); break;
			default: setLast14Days(); break;
		}
	}

	const resetTabs = () => {
		setCurrentTab(null);
	}

	const changeTabHandler = (tab = null) => {
		if (!tab) return false;
		setCurrentTab(tab);
	}

	const initDefaultDate = () => {
		setDateConfigs((prevState) => ({
			...prevState,
			dateFilterType: 'last_14',
		}));

		setLast14Days();
	}

	const groupByChunks = (arr, n) => {
		let group = [];
		for (let i = 0, end = arr.length / n; i < end; ++i)
			group.push(arr.slice(i * n, (i + 1) * n));
		return group;
	}


	const buildGroupData = (subgroup = [], singular = false) => {
		let groupObj;
		if(singular){
			groupObj = {
				startDate : subgroup.lastRead,
				endDate : null,
				consumption : subgroup.last24HourConsumption,
				temperature : Math.round(parseFloat(subgroup.temperature)),
				signalStrength : parseInt(subgroup.signalStrength),
				lastLevel : parseFloat(subgroup.lastLevel),
				lowLevel : parseInt(subgroup.lowAlarmThreshold),
				criticalLevel : parseInt(subgroup.criticalLowAlarmThreshold),
				hoursToEmpty : subgroup.hoursToEmpty
			}
		} else {
			const groupStartDate = subgroup[0].lastRead;
			const groupEndDate = subgroup[subgroup.length - 1].lastRead;

			const sumConsumption = subgroup.map(i=> i.last24HourConsumption).reduce((a,b)=>a+b);

			const sumTemperature = subgroup.map(i=> parseFloat(i.temperature)).reduce((a,b)=>a+b);
			const averageTemperature = (Math.round(sumTemperature / subgroup.length)) || 0;

			const sumSignalStrength = subgroup.map(i=> parseInt(i.signalStrength)).reduce((a,b)=>a+b);
			const averageSignalStrength = (Math.round(sumSignalStrength / subgroup.length)) || 0;

			const sumLevel = subgroup.map(i=> parseFloat(i.lastLevel)).reduce((a,b)=>a+b);
			const averageLevel = (sumLevel / subgroup.length) || 0;

			groupObj = {
				startDate : groupStartDate,
				endDate : groupEndDate,
				consumption : sumConsumption,
				temperature : averageTemperature,
				signalStrength : averageSignalStrength,
				lastLevel : averageLevel,
				lowLevel : parseInt(subgroup[0].lowAlarmThreshold),
				criticalLevel : parseInt(subgroup[0].criticalLowAlarmThreshold),
				hoursToEmpty : subgroup[subgroup.length - 1].hoursToEmpty
			}
		}

		return groupObj;
	}

	const amalgamatedData = useMemo( () => {
		// Group data into chunks in order to make it more readable in the graph
		const startDate = new Date(dateConfigs.startDate).getTime();
		const endDate = new Date(dateConfigs.endDate).getTime();
		const totalDays = Math.round(Math.abs((endDate - startDate) / (24 * 60 * 60 * 1000)));

		// grouping data
		let groups = [];
		let subgroups = [];
		if(totalDays <= 24){
			data.forEach( (item) => {
				groups.push(buildGroupData(item,true));
			});
		} else {
		 	if(totalDays <= 100){ subgroups = groupByChunks(data,7); } // weeks
			else if(totalDays <= 550){ subgroups = groupByChunks(data,30); } // months
			else{ subgroups = groupByChunks(data,365); } // years

			subgroups.forEach( (subgroup) => {
				groups.push(buildGroupData(subgroup,false));
			});
		}

		//console.log('Total days selected :',totalDays);
		//console.log('Subgroups :', groups);
		return groups;
	},[data]);

	const tabsSharedProps = {
		dateConfigs : dateConfigs,
		readingDevice : readingDevice,
		enableCurrentPeriod : false,
		setStartDate : setStartDate,
		setEndDate : setEndDate,
		changeDateFilterType : setDateFilterType,
		refreshTabs: changeTabHandler,
		loading : loading,
	}

	useEffect(() => {
		initDefaultDate();
	}, []);

	useEffect(async () => {
		if(!readingDevice || !dateConfigs.endDate) return;
		setLoading(true);
		await getReadingData().then(r => {
			setLoading(false);
			if(r) setData(r);
		})
	}, [dateConfigs]);


	return (
		<Tabs currentTab={currentTab} reset={resetTabs}>
			<div label={t("tanks.details.tabs.level")} tabkey="level">
				<LevelTab {...tabsSharedProps} data={amalgamatedData} />
			</div>
			<div label={t("tanks.details.tabs.consumption")} tabkey="consumption">
				<ConsumptionTab {...tabsSharedProps} data={amalgamatedData} />
			</div>
			<div label={t("tanks.details.tabs.fill")} tabkey="fill">
				<FillingsTab {...tabsSharedProps} data={unparsedData} />
			</div>
			<div label={t("tanks.details.tabs.signal")} tabkey="signal">
				<SignalTab {...tabsSharedProps} data={amalgamatedData} />
			</div>
			<div label={t("tanks.details.tabs.stats")} tabkey="stats">
				<StatsTab {...tabsSharedProps} data={unparsedData} totalPages={totalPages} />
			</div>
		</Tabs>
	);
};

export default TankTabs;
